// External checkout redirect utility
// Redirects to ACC domain signup flow with the same params
// Captures and forwards all tracking parameters from first user visit

import { trackExternalRedirect } from '@/components/analytics/FlowTracker';

const ACC_SIGNUP_BASE_URL = 'https://acc.enkelenergi.dk/signup/';
const TRACKING_STORAGE_KEY = 'enkel-tracking-params';
const GA_MEASUREMENT_ID = 'G-MTSDHXEHBH';
const PRODUCT_PRESELECT_KEY = 'enkel-product-preselect';

/**
 * Safe localStorage.setItem — clears stale enkel-* keys if quota is exceeded, then retries once.
 * Silently swallows errors if storage is still full after cleanup.
 */
function safeLocalStorageSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    if (e instanceof DOMException && (e.name === 'QuotaExceededError' || e.code === 22)) {
      // Clear any stale keys we own to free space
      const ownedPrefixes = ['enkel-', 'flow_', 'first_touch_attribution'];
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && ownedPrefixes.some(p => k.startsWith(p)) && k !== key) toRemove.push(k);
      }
      toRemove.forEach(k => localStorage.removeItem(k));
      try { localStorage.setItem(key, value); } catch {}
    }
  }
}

/**
 * Generates a unique Base44 click ID for deduplication
 * Format: b44_YYYYMMDDHHMMSS_RANDOM (e.g., b44_20260121143052_x7k9m)
 * If deal300 is true, appends _deal300 suffix (e.g., b44_20260121143052_x7k9m_deal300)
 */
function generateB44ClickId(deal300 = false) {
  const now = new Date();
  const timestamp = now.getFullYear().toString() +
    String(now.getMonth() + 1).padStart(2, '0') +
    String(now.getDate()).padStart(2, '0') +
    String(now.getHours()).padStart(2, '0') +
    String(now.getMinutes()).padStart(2, '0') +
    String(now.getSeconds()).padStart(2, '0');
  const random = Math.random().toString(36).substring(2, 7);
  const base = `b44_${timestamp}_${random}`;
  return deal300 ? `${base}_deal300` : base;
}

/**
 * Gets the Google Analytics/Ads cross-domain linker parameter (_gl)
 * Uses multiple methods to ensure we get the linker param for cross-domain tracking
 * Returns a promise that resolves to the decorated URL
 */
function decorateUrlWithLinker(url) {
  return new Promise((resolve) => {
    // Timeout after 1.5 seconds to not block the redirect indefinitely
    const timeout = setTimeout(() => {
      resolve(url);
    }, 1500);

    // Method 1: Use gtagLinker if available (set by GTM Conversion Linker)
    if (window.gtagLinker && typeof window.gtagLinker === 'function') {
      try {
        const decorated = window.gtagLinker(url);
        if (decorated && decorated !== url) {
          // Decorated via gtagLinker
          clearTimeout(timeout);
          resolve(decorated);
          return;
        }
      } catch (e) {
        // gtagLinker error
      }
    }

    // Method 2: Create a temporary <a> element and let GTM decorate it
    // This leverages GTM's automatic link decoration
    try {
      const tempLink = document.createElement('a');
      tempLink.href = url;
      tempLink.style.display = 'none';
      document.body.appendChild(tempLink);
      
      // Dispatch a mousedown event to trigger GTM's link decoration
      const event = new MouseEvent('mousedown', {
        bubbles: true,
        cancelable: true,
        view: window
      });
      tempLink.dispatchEvent(event);
      
      // Small delay to allow GTM to decorate the link
      setTimeout(() => {
        const decoratedUrl = tempLink.href;
        document.body.removeChild(tempLink);
        
        if (decoratedUrl.includes('_gl=')) {
          // Decorated via temp link
          clearTimeout(timeout);
          resolve(decoratedUrl);
        } else {
          // Method 3: Try gtag get linker command
          if (typeof window.gtag === 'function') {
            window.gtag('get', GA_MEASUREMENT_ID, 'linker', (linkerParam) => {
              if (linkerParam) {
                const separator = url.includes('?') ? '&' : '?';
                const finalUrl = url + separator + '_gl=' + encodeURIComponent(linkerParam);
                // Decorated via gtag get linker
                clearTimeout(timeout);
                resolve(finalUrl);
              } else {
                clearTimeout(timeout);
                resolve(url);
              }
            });
          } else {
            clearTimeout(timeout);
            resolve(url);
          }
        }
      }, 100);
    } catch (e) {
      // Temp link method error
      clearTimeout(timeout);
      resolve(url);
    }
  });
}

/**
 * Generates the t= tracking parameter based on product configuration
 * Reads the final URL params and creates a unique numeric tracking code
 * 
 * Mapping (designed for Google Ads "URL contains" matching):
 * t=A : El-only
 * t=B : El-only + Forbrugsafregning
 * t=C : El-only + Service Agreement
 * t=D : El-only + Forbrugsafregning + Service Agreement
 * t=E : El + Charger
 * t=F : El + Charger + Forbrugsafregning
 * t=G : El + Charger + Service Agreement
 * t=H : El + Charger + Forbrugsafregning + Service Agreement
 * t=J : El + NexBlue Edge 2 + Smart Sensor
 * t=K : El + NexBlue Edge 2 + Forbrugsafregning + Smart Sensor
 * t=L : El + NexBlue Edge 2 + Service Agreement + Smart Sensor
 * t=M : El + NexBlue Edge 2 + Forbrugsafregning + Service Agreement + Smart Sensor
 */
function generateTrackingParam(urlString) {
  // Parse the URL to extract params
  const url = new URL(urlString);
  const params = url.searchParams;
  
  const product = parseInt(params.get('product') || '0', 10);
  const subscriptionF = params.get('subscription_f') === '1';
  const serviceAgreement = params.get('service_agreement') === '1';
  const smartSensor = params.get('smart_sensor') === '1';
  
  const hasCharger = product >= 1;
  const hasSmartSensor = smartSensor && product === 2;
  
  // Determine tracking code based on combination
  if (hasSmartSensor) {
    // Smart sensor combinations (J, K, L, M)
    if (subscriptionF && serviceAgreement) return 'M';
    if (serviceAgreement) return 'L';
    if (subscriptionF) return 'K';
    return 'J';
  } else if (hasCharger) {
    // Charger combinations without smart sensor (E, F, G, H)
    if (subscriptionF && serviceAgreement) return 'H';
    if (serviceAgreement) return 'G';
    if (subscriptionF) return 'F';
    return 'E';
  } else {
    // El-only combinations (A, B, C, D)
    if (subscriptionF && serviceAgreement) return 'D';
    if (serviceAgreement) return 'C';
    if (subscriptionF) return 'B';
    return 'A';
  }
}

// List of tracking parameters to capture and forward
const TRACKING_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',      // Google Ads click ID
  'gbraid',     // Google Ads iOS click ID (privacy-preserving)
  'wbraid',     // Google Ads web click ID (privacy-preserving)
  'fbclid',     // Facebook click ID
  'msclkid',    // Microsoft Ads click ID
  'ttclid',     // TikTok click ID
  'ref',        // General referral
  'affiliate',  // Affiliate ID
  'partner',    // Partner ID
  'source',     // Alternative source param
  'medium',     // Alternative medium param
  'campaign',   // Alternative campaign param
  'affiliate_click_id', // External affiliate click ID for postback tracking
  'clickid',    // Alternative affiliate click ID param
  'click_id',   // Alternative affiliate click ID param
  'paid',       // Partner-ads partner ID
  'pacid',      // Partner-ads click ID
];

/**
 * Intermediate flow parameters for /loesning page
 * These are user-friendly parameters that /loesning translates to technical params
 */
const LOESNING_PARAMS = [
  'intent',            // Flow intent: 'ev', 'solar', 'electricity_only', 'general'
  'has_ev',            // Has EV: '0' or '1'
  'has_solar',         // Has solar: '0' or '1'
  'has_heat_pump',     // Has heat pump: '0' or '1'
  'housing_type',      // Housing: 'summerhouse', 'apartment', 'house'
  'consumption',       // Annual kWh consumption
  'needs_charger',     // Needs charger: '0' or '1'
  'charger_compatible',// Has compatible charger: '0' or '1'
  'charger',           // Charger selection: '1', '2', '3' (for pre-selected charger)
  'installation',      // Installation code: '0', '1', '2'
  'smart_sensor',      // Smart sensor: '0' or '1'
  'show_solution',     // Skip flow, show solution: '0' or '1'
  'subscription',      // Subscription code: '1'=kvartal, '2'=strøm, '3'=natur
  'subscription_f',    // Forbrugsafregning: '0' or '1'
  'subscription_solar',// Solar agreement: '0' or '1'
  'service_agreement', // Service agreement: '0' or '1'
];

/**
 * LAST-CLICK ATTRIBUTION MODEL
 * 
 * When a user arrives with new tracking params (click IDs or UTMs), we REPLACE
 * all previous attribution data. This prevents forwarding stale click IDs from
 * earlier touchpoints (e.g. sending both gclid AND fbclid to checkout when the
 * user clicked Google first, then Facebook — only Facebook should get credit).
 * 
 * The b44_click_id is always preserved (it's our own dedup key, not channel attribution).
 * Partner-ads (_pa_ suffix on b44) is preserved unless new partner-ads params arrive.
 */

// Click ID params that indicate a new paid click (each belongs to one channel)
const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid', 'affiliate_click_id', 'clickid', 'click_id', 'pacid'];

// First-touch storage key — never wiped, survives last-click attribution
const FIRST_TOUCH_KEY = 'enkel-first-touch';

// Priority order for embedding the active click ID into the b44 click ID.
// Last-click model means at most one channel's click ID is stored at a time.
const CID_EMBED_PRIORITY = [
  ['gclid', 'gclid'],
  ['gbraid', 'gbraid'],
  ['wbraid', 'wbraid'],
  ['msclkid', 'msclkid'],
  ['fbclid', 'fbclid'],
  ['ttclid', 'ttclid'],
  ['affiliate_click_id', 'affid'],
  ['clickid', 'affid'],
  ['click_id', 'affid'],
];

/**
 * Embeds the active click ID into the OUTGOING b44 click ID as a final
 * `_cid_<key>_<value>` suffix, so it survives the external redirect even when
 * the redirecter strips every query param except b44 + source/medium.
 * Applied only to the URL copy at redirect time — the STORED b44 id stays clean,
 * matching what FlowAnalytics recorded on-site. recordSaleEvent strips the
 * suffix back off and restores the click ID into its dedicated field.
 */
function appendClickIdToB44(b44Id) {
  if (!b44Id) return b44Id;
  // Tag EN-site visitors with _lng_en so conversion language survives the external
  // redirect even when all query params are stripped. recordSaleEvent strips the
  // tag back off and stores it in the dedicated page_lang field.
  const currentLang = localStorage.getItem('enkel-lang') || 'da';
  if (currentLang === 'en' && !b44Id.includes('_lng_en')) {
    b44Id = b44Id + '_lng_en';
  }
  if (b44Id.includes('_cid_')) return b44Id;
  const trackingData = getStoredTrackingParams();
  for (const [param, key] of CID_EMBED_PRIORITY) {
    if (trackingData[param]) {
      return `${b44Id}_cid_${key}_${trackingData[param]}`;
    }
  }
  return b44Id;
}

// UTM params that indicate a new campaign touchpoint
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

// All attribution params that get WIPED on a new click (everything except structural/metadata)
const ATTRIBUTION_PARAMS = [...CLICK_ID_PARAMS, ...UTM_PARAMS, 'source', 'medium', 'campaign', 'ref', 'affiliate', 'partner', 'paid'];

/**
 * Captures tracking parameters from URL on first visit and stores them
 * Should be called on app initialization (e.g., in Layout)
 * Also generates a b44 click ID on first visit for conversion tracking
 * 
 * LAST-CLICK MODEL: New click IDs or UTMs wipe ALL previous attribution.
 */
export function captureTrackingParams() {
  const urlParams = new URLSearchParams(window.location.search);
  const existingParams = getStoredTrackingParams();
  
  // Capture MakeInfluence _miid cookie if present
  const miidCookie = document.cookie.split(';').find(c => c.trim().startsWith('_miid='));
  const miidValue = miidCookie ? miidCookie.split('=')[1] : null;
  
  // Detect if this pageload carries new attribution signals
  const hasNewClickId = CLICK_ID_PARAMS.some(p => urlParams.has(p));
  const hasNewUtm = UTM_PARAMS.some(p => urlParams.has(p));
  const hasNewAttribution = hasNewClickId || hasNewUtm;
  const isFirstVisit = !existingParams.capturedAt;
  const hasNewReferrer = document.referrer && !existingParams.referrer;
  
  if (hasNewAttribution || isFirstVisit || hasNewReferrer || miidValue) {
    // START FRESH for attribution fields when new tracking arrives
    // Keep only structural data (b44_click_id, landingPage on first visit)
    const trackingData = {};
    
    // Preserve b44 click ID (our own key — not channel attribution)
    // If we have a MakeInfluence _miid cookie, embed it in the b44 click ID
    const baseClickId = existingParams.b44_click_id || generateB44ClickId();
    if (miidValue && !baseClickId.includes('_mi_')) {
      trackingData.b44_click_id = `${baseClickId}_mi_${miidValue}`;
    } else {
      trackingData.b44_click_id = baseClickId;
    }
    
    // Preserve pa_landed_at for partner-ads 14-day window
    if (existingParams.pa_landed_at) {
      trackingData.pa_landed_at = existingParams.pa_landed_at;
    }
    
    // Metadata: keep first-visit landing page, update on new attribution
    trackingData.capturedAt = existingParams.capturedAt || new Date().toISOString();
    trackingData.lastAttributionAt = new Date().toISOString();
    trackingData.landingPage = hasNewAttribution 
      ? window.location.pathname 
      : (existingParams.landingPage || window.location.pathname);
    
    // Referrer: capture external referrer, skip internal navigation referrers
    const currentReferrer = document.referrer || null;
    const isInternalReferrer = currentReferrer && (
      currentReferrer.includes('enkelenergi.dk') || 
      currentReferrer.includes('base44.app') ||
      currentReferrer.includes('localhost')
    );
    if (hasNewAttribution && currentReferrer && !isInternalReferrer) {
      trackingData.referrer = currentReferrer;
    } else {
      trackingData.referrer = existingParams.referrer || currentReferrer || null;
    }
    
    // Capture ONLY params present in this URL — old ones are intentionally dropped
    TRACKING_PARAMS.forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        trackingData[param] = value;
      }
    });
    
    // Handle partner-ads paid/pacid → embed in b44 click ID
    const paidParam = urlParams.get('paid');
    const pacidParam = urlParams.get('pacid');
    if (paidParam && pacidParam && !trackingData.b44_click_id.includes('_pa_')) {
      const baseId = trackingData.b44_click_id.replace(/_deal300$/, '').replace(/_[A-Z0-9_]+_(?:nexblue_edge_2|zaptec_go|zaptec_go_2)$/, '');
      trackingData.b44_click_id = `${baseId}_pa_${paidParam}_${pacidParam}`;
      trackingData.pa_landed_at = new Date().toISOString();
    }
    
    // Handle Readpeak clickid → embed in b44 click ID as _rp_CLICKID
    const rpClickId = urlParams.get('clickid');
    const isReadpeakTraffic = (urlParams.get('utm_source') || '').toLowerCase() === 'readpeak' || 
                               (urlParams.get('utm_medium') || '').toLowerCase() === 'native';
    if (rpClickId && isReadpeakTraffic && !trackingData.b44_click_id.includes('_rp_')) {
      const baseId = trackingData.b44_click_id.replace(/_deal300$/, '').replace(/_[A-Z0-9_]+_(?:nexblue_edge_2|zaptec_go|zaptec_go_2)$/, '');
      trackingData.b44_click_id = `${baseId}_rp_${rpClickId}`;
    }
    
    sessionStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(trackingData));
    safeLocalStorageSet(TRACKING_STORAGE_KEY, JSON.stringify(trackingData));
  } else if (!existingParams.b44_click_id) {
    // Edge case: existing params but no b44 click ID (old sessions)
    const updatedParams = {
      ...existingParams,
      b44_click_id: generateB44ClickId()
    };
    sessionStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(updatedParams));
    safeLocalStorageSet(TRACKING_STORAGE_KEY, JSON.stringify(updatedParams));
  }
  
  // Check partner-ads 14-day attribution window expiry
  const currentParams = getStoredTrackingParams();
  if (currentParams.pa_landed_at && currentParams.b44_click_id?.includes('_pa_')) {
    const landedAt = new Date(currentParams.pa_landed_at);
    const daysSinceLanding = (Date.now() - landedAt.getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceLanding > 14) {
      const cleanedId = currentParams.b44_click_id.replace(/_pa_[^_]+_.+$/, '');
      currentParams.b44_click_id = cleanedId;
      delete currentParams.pa_landed_at;
      delete currentParams.paid;
      delete currentParams.pacid;
      sessionStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(currentParams));
      safeLocalStorageSet(TRACKING_STORAGE_KEY, JSON.stringify(currentParams));
    }
  }
  
  // Store first-touch data — only on the very first visit, never overwrite
  captureFirstTouch(urlParams);

  // Also capture /loesning flow params (separate from tracking)
  captureLoesningParams();
}

/**
 * Captures and persists first-touch attribution — NEVER overwritten.
 * This survives all subsequent last-click overwrites.
 */
function captureFirstTouch(urlParams) {
  const existing = localStorage.getItem(FIRST_TOUCH_KEY);
  if (existing) return; // Already have first touch — never overwrite

  // Determine source/medium for this first visit
  const utmSource = urlParams.get('utm_source') || '';
  const utmMedium = urlParams.get('utm_medium') || '';
  const utmCampaign = urlParams.get('utm_campaign') || '';
  const referrer = document.referrer || '';
  let referrerDomain = '';
  try { referrerDomain = new URL(referrer).hostname.replace('www.', ''); } catch {}

  // Only store if there's something meaningful (not pure direct with no info)
  const hasAttribution = utmSource || utmMedium || urlParams.get('gclid') || urlParams.get('fbclid') ||
    urlParams.get('msclkid') || urlParams.get('ttclid') || referrerDomain;

  if (!hasAttribution) {
    // Still store direct as first touch to lock it
    safeLocalStorageSet(FIRST_TOUCH_KEY, JSON.stringify({
      first_touch_source: 'direct',
      first_touch_medium: 'none',
      first_touch_campaign: '',
      first_touch_referrer: '',
      first_touch_landing: window.location.pathname,
      first_touch_date: new Date().toISOString(),
    }));
      return;
    }

    // Derive source if no UTM
    let derivedSource = utmSource;
    let derivedMedium = utmMedium;
    if (!derivedSource) {
      if (urlParams.get('gclid')) { derivedSource = 'google'; derivedMedium = derivedMedium || 'cpc'; }
      else if (urlParams.get('msclkid')) { derivedSource = 'bing'; derivedMedium = derivedMedium || 'cpc'; }
      else if (urlParams.get('fbclid')) { derivedSource = 'facebook'; derivedMedium = derivedMedium || 'paid_social'; }
      else if (urlParams.get('ttclid')) { derivedSource = 'tiktok'; derivedMedium = derivedMedium || 'paid_social'; }
      else if (referrerDomain) {
        if (referrerDomain.includes('readpeak')) { derivedSource = 'readpeak'; derivedMedium = 'native'; }
        else if (referrerDomain.includes('google')) { derivedSource = 'google'; derivedMedium = 'organic'; }
        else if (referrerDomain.includes('bing') || referrerDomain.includes('msn')) { derivedSource = 'bing'; derivedMedium = 'organic'; }
        else if (referrerDomain.includes('facebook') || referrerDomain.includes('fb.com')) { derivedSource = 'facebook'; derivedMedium = 'social'; }
        else if (referrerDomain.includes('instagram')) { derivedSource = 'instagram'; derivedMedium = 'social'; }
        else if (referrerDomain.includes('linkedin')) { derivedSource = 'linkedin'; derivedMedium = 'social'; }
        else if (referrerDomain.includes('twitter') || referrerDomain.includes('t.co')) { derivedSource = 'twitter'; derivedMedium = 'social'; }
        else { derivedSource = referrerDomain; derivedMedium = 'referral'; }
      } else {
        derivedSource = 'direct';
        derivedMedium = 'none';
      }
    }

    safeLocalStorageSet(FIRST_TOUCH_KEY, JSON.stringify({
      first_touch_source: derivedSource,
      first_touch_medium: derivedMedium,
      first_touch_campaign: utmCampaign,
      first_touch_referrer: referrerDomain,
      first_touch_landing: window.location.pathname,
      first_touch_date: new Date().toISOString(),
    }));
}

/**
 * Gets stored first-touch attribution data
 */
export function getFirstTouchData() {
  const data = localStorage.getItem(FIRST_TOUCH_KEY);
  if (!data) return {};
  try { return JSON.parse(data); } catch { return {}; }
}

/**
 * Captures /loesning flow parameters from URL and stores them
 * These persist during session and are used to pre-fill /loesning page
 */
export function captureLoesningParams() {
  const urlParams = new URLSearchParams(window.location.search);
  const existingParams = getStoredLoesningParams();
  
  // Check if any loesning params are in the URL
  const hasLoesningParams = LOESNING_PARAMS.some(param => urlParams.has(param));
  
  if (hasLoesningParams) {
    const loesningData = {
      ...existingParams,
      capturedAt: new Date().toISOString(),
      capturedOnPage: window.location.pathname,
    };
    
    // Capture all loesning params from URL
    LOESNING_PARAMS.forEach(param => {
      const value = urlParams.get(param);
      if (value !== null) {
        loesningData[param] = value;
      }
    });
    
    // Store in sessionStorage only (should persist during navigation but not across sessions)
    sessionStorage.setItem(PRODUCT_PRESELECT_KEY, JSON.stringify(loesningData));
  }
}

/**
 * Gets stored /loesning flow parameters
 */
export function getStoredLoesningParams() {
  const sessionData = sessionStorage.getItem(PRODUCT_PRESELECT_KEY);
  if (sessionData) {
    try {
      return JSON.parse(sessionData);
    } catch (e) {
      // Invalid JSON, ignore
    }
  }
  return {};
}

/**
 * Updates stored /loesning parameters with new values
 * Use this when user makes selections in the flow
 * Only updates fields that are provided - existing values are preserved
 */
export function updateLoesningParams(updates) {
  const existing = getStoredLoesningParams();
  const updated = { 
    ...existing, 
    ...updates,
    updatedAt: new Date().toISOString(),
    updatedOnPage: window.location.pathname
  };
  sessionStorage.setItem(PRODUCT_PRESELECT_KEY, JSON.stringify(updated));
}

/**
 * Clears /loesning parameters (e.g., after checkout)
 */
export function clearLoesningParams() {
  sessionStorage.removeItem(PRODUCT_PRESELECT_KEY);
}

/**
 * Tags the stored b44_click_id with a manually self-reported referrer K-number.
 * Embeds it as _hvm_<KNUMBER> (henvisning manual) so it survives last-click attribution
 * and rides through the external checkout inside the b44 click ID — exactly like _hv_/_pa_/_rp_.
 * This is the strongest referral signal because the end user typed it themselves.
 * Replaces any previous _hvm_ tag so the latest entry wins.
 */
export function tagReferrerKNumber(kNumber) {
  const clean = (kNumber || '').trim().toUpperCase();
  if (!clean) return;
  const params = getStoredTrackingParams();
  if (!params.b44_click_id) return;
  // Strip any existing _hvm_ tag, then append the new one
  params.b44_click_id = params.b44_click_id.replace(/_hvm_[A-Z0-9]+/i, '') + `_hvm_${clean}`;
  sessionStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(params));
  safeLocalStorageSet(TRACKING_STORAGE_KEY, JSON.stringify(params));
}

/**
 * Tags the stored b44_click_id with a suffix (e.g., '_bill') for attribution tracking.
 * Only appends if the suffix isn't already present. Preserves existing suffixes like _pa_, _rp_, _deal300.
 * The tag survives last-click attribution since it lives on the b44 click ID itself.
 */
export function tagB44ClickId(suffix) {
  const params = getStoredTrackingParams();
  if (!params.b44_click_id) return;
  if (params.b44_click_id.includes(suffix)) return; // already tagged
  params.b44_click_id = params.b44_click_id + suffix;
  sessionStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(params));
  safeLocalStorageSet(TRACKING_STORAGE_KEY, JSON.stringify(params));
}

/**
 * Gets stored tracking parameters (prefers session, falls back to localStorage)
 */
export function getStoredTrackingParams() {
  const sessionData = sessionStorage.getItem(TRACKING_STORAGE_KEY);
  if (sessionData) {
    try {
      return JSON.parse(sessionData);
    } catch (e) {
      // Invalid JSON, ignore
    }
  }
  
  const localData = localStorage.getItem(TRACKING_STORAGE_KEY);
  if (localData) {
    try {
      return JSON.parse(localData);
    } catch (e) {
      // Invalid JSON, ignore
    }
  }
  
  return {};
}

/**
 * Appends stored tracking params to a URLSearchParams object
 */
function appendTrackingParams(searchParams) {
  const trackingData = getStoredTrackingParams();
  
  // Append all tracking params that exist
  TRACKING_PARAMS.forEach(param => {
    if (trackingData[param]) {
      searchParams.set(param, trackingData[param]);
    }
  });
  
  // Also append metadata
  if (trackingData.landingPage) {
    searchParams.set('landing_page', trackingData.landingPage);
  }
  if (trackingData.referrer) {
    searchParams.set('referrer', trackingData.referrer);
  }
}

/**
 * Builds the external signup URL with the given params and redirects in the same window
 * @param {Object} params - The checkout parameters
 * @param {string|number} params.product - Product code (0=el-only, 1=zaptec-go, 2=nexblue, 3=zaptec-go-2)
 * @param {boolean} params.deal300 - Whether this is the 300kr NexBlue deal (tracked in b44 click ID)
 * @param {string} params.campaignTrackingCode - Campaign tracking suffix for b44 click ID (e.g. 'FLASH500FEB_nexblue_edge_2')
 * @param {string|number} params.installation - Installation option code
 * @param {string|number} params.subscription_code - Subscription code (1=kvartal, 2=strøm, 3=natur)
 * @param {string|number} params.subscription_f - Forbrugsafregning flag (0 or 1)
 * @param {string|number} params.subscription_solar - Solar agreement flag (0 or 1)
 * @param {string|number} params.serviceagreement - Service agreement flag (0 or 1)
 * @param {Object} params.pricing - Pricing breakdown object (optional)
 */
export function redirectToExternalCheckout(params) {
  // Funnel analytics: this is the last thing we can see before the visitor leaves for signup.
  try {
    sessionStorage.setItem('checkout_redirected', '1');
    trackExternalRedirect('acc_signup', {
      product: params.product ?? 0,
      installation: params.installation ?? 0,
      subscription: params.subscription_code ?? 2,
      service_agreement: params.serviceagreement ? 1 : 0,
      charger_situation: params.chargerSituation || '',
    });
  } catch {}

  // Build URL with ordered params: product params first, then tracking, then pricing last
  let url = ACC_SIGNUP_BASE_URL + '?';
  
  // 1. Product and subscription params first
  const productParams = [];
  productParams.push(`product=${params.product ?? 0}`);
  productParams.push(`installation=${params.installation ?? 0}`);
  // Note: ACC uses 'subscription' not 'subscription_code'
  productParams.push(`subscription=${params.subscription_code ?? 2}`);
  productParams.push(`subscription_f=${params.subscription_f ? 1 : 0}`);
  productParams.push(`subscription_solar=${params.subscription_solar ? 1 : 0}`);

  productParams.push(`service_agreement=${params.serviceagreement ? 1 : 0}`);
  // smart_sensor param disabled via feature flag - always send 0
  productParams.push(`smart_sensor=0`);
  
  // 2. Lang param
  const currentLang = localStorage.getItem('enkel-lang') || 'da';
  if (currentLang === 'en') {
    productParams.push('lang=en');
  }
  
  url += productParams.join('&');
  
  // 3. Tracking parameters (exclude paid/pacid — they're embedded in b44 click ID)
  const trackingData = getStoredTrackingParams();
  const trackingParams = [];
  const SKIP_IN_URL = ['paid', 'pacid', 'clickid']; // Partner-ads & Readpeak params travel inside b44 click ID
  
  TRACKING_PARAMS.forEach(param => {
    if (trackingData[param] && !SKIP_IN_URL.includes(param)) {
      trackingParams.push(`${param}=${encodeURIComponent(trackingData[param])}`);
    }
  });
  
  // Append metadata (stored as camelCase landingPage — sent as landing_page)
  if (trackingData.landingPage) {
    trackingParams.push(`landing_page=${encodeURIComponent(trackingData.landingPage)}`);
  }
  if (trackingData.referrer) {
    trackingParams.push(`referrer=${encodeURIComponent(trackingData.referrer)}`);
  }
  
  // Handle affiliate_click_id (may come as clickid or click_id)
  const affiliateClickId = trackingData.affiliate_click_id || trackingData.clickid || trackingData.click_id;
  if (affiliateClickId && !trackingParams.some(p => p.startsWith('affiliate_click_id='))) {
    trackingParams.push(`affiliate_click_id=${encodeURIComponent(affiliateClickId)}`);
  }

  // Append first-touch data — these survive last-click attribution wipes
  const firstTouchData = getFirstTouchData();
  if (firstTouchData.first_touch_source) {
    trackingParams.push(`first_touch_source=${encodeURIComponent(firstTouchData.first_touch_source)}`);
    trackingParams.push(`first_touch_medium=${encodeURIComponent(firstTouchData.first_touch_medium || '')}`);
    if (firstTouchData.first_touch_campaign) trackingParams.push(`first_touch_campaign=${encodeURIComponent(firstTouchData.first_touch_campaign)}`);
    if (firstTouchData.first_touch_referrer) trackingParams.push(`first_touch_referrer=${encodeURIComponent(firstTouchData.first_touch_referrer)}`);
    if (firstTouchData.first_touch_landing) trackingParams.push(`first_touch_landing=${encodeURIComponent(firstTouchData.first_touch_landing)}`);
    if (firstTouchData.first_touch_date) trackingParams.push(`first_touch_date=${encodeURIComponent(firstTouchData.first_touch_date)}`);
  }

  if (trackingParams.length > 0) {
    url += '&' + trackingParams.join('&');
  }
  
  // 4. Pricing breakdown as single pipe-delimited parameter (ALWAYS LAST)
  // Format: consumption|zone|pricePerKwh|spotInclVat|spotExVat|surcharge|subMonthly|forbrug|distTariff|distSub|distTotal|transTariff|transSub|transTotal|elafgift|stategrid|dso
  if (params.pricing) {
    const p = params.pricing;
    const pxValues = [
      p.consumption || '',
      p.zone || '',
      p.pricePerKwh || '',
      p.spot || '',
      p.spotEx || '',
      p.surcharge || '0.00',
      p.sub || '',
      p.forbrug || '0.00',
      p.distTariff || '',
      p.distSub || '',
      p.distTotal || '',
      p.transTariff || '',
      p.transSub || '',
      p.transTotal || '',
      p.elafgift || '',
      p.stategrid || '',
      (p.dso || '').replace(/\s+/g, '-') // Replace spaces with hyphens
    ];
    url += '&px=' + encodeURIComponent(pxValues.join('|'));
  }
  
  // Generate t= tracking param based on final URL and prepend it
  const trackingCode = generateTrackingParam(url);
  
  // Use b44 click ID from session (generated on landing) or generate new one as fallback
  // This ensures the same ID is used from landing through checkout for attribution
  const storedB44 = getStoredTrackingParams().b44_click_id;
  let b44ClickId = storedB44 || generateB44ClickId(params.deal300 === true);
  
  // Campaign tracking takes priority over deal300
  // IMPORTANT: Never strip _pa_ or _rp_ suffix — partner tracking must survive
  if (params.campaignTrackingCode && !b44ClickId.includes('_pa_') && !b44ClickId.includes('_rp_')) {
    // Strip any existing deal/campaign suffixes from stored ID
    const baseId = b44ClickId.replace(/_deal300$/, '').replace(/_[A-Z0-9_]+_(?:nexblue_edge_2|zaptec_go|zaptec_go_2)$/, '');
    b44ClickId = `${baseId}_${params.campaignTrackingCode}`;
  } else if (params.deal300 === true && storedB44 && !storedB44.includes('_deal300') && !b44ClickId.includes('_pa_') && !b44ClickId.includes('_rp_')) {
    b44ClickId = storedB44 + '_deal300';
  }
  
  // Embed the active click ID as the FINAL suffix so it survives param-stripping redirects
  b44ClickId = appendClickIdToB44(b44ClickId);
  
  // Insert t= and b44= as the first parameters after the ?
  const urlParts = url.split('?');
  let finalUrl = urlParts[0] + '?t=' + trackingCode + '&b44=' + encodeURIComponent(b44ClickId) + '&' + urlParts[1];
  
  // Get Google Analytics/Ads linker parameter for cross-domain tracking
  // This ensures the _gl parameter is passed for conversion tracking
  // CRITICAL: Hard fallback ensures redirect ALWAYS happens even if linker fails
  let redirected = false;
  const doRedirect = (url, source) => {
    if (redirected) return;
    redirected = true;
    window.location.href = url;
  };

  // Hard fallback: redirect without _gl after 2.5s no matter what
  setTimeout(() => doRedirect(finalUrl, 'timeout_fallback'), 2500);

  try {
    decorateUrlWithLinker(finalUrl).then((decoratedUrl) => {
      doRedirect(decoratedUrl, 'linker_decorated');
    }).catch(() => {
      doRedirect(finalUrl, 'linker_promise_error');
    });
  } catch (e) {
    // decorateUrlWithLinker error
    doRedirect(finalUrl, 'linker_sync_error');
  }
}

/**
 * Builds the external signup URL string (for cases where you need the URL without redirecting)
 */
export function buildExternalCheckoutUrl(params) {
  // Build URL with ordered params: product params first, then tracking, then pricing last
  let url = ACC_SIGNUP_BASE_URL + '?';
  
  // 1. Product and subscription params first
  const productParams = [];
  if (params.product !== undefined) productParams.push(`product=${params.product}`);
  if (params.installation !== undefined) productParams.push(`installation=${params.installation}`);
  if (params.subscription_code !== undefined) productParams.push(`subscription=${params.subscription_code}`);
  if (params.subscription_f !== undefined) productParams.push(`subscription_f=${params.subscription_f}`);
  productParams.push(`subscription_solar=${params.subscription_solar ? 1 : 0}`);

  productParams.push(`service_agreement=${params.serviceagreement ? 1 : 0}`);
  // smart_sensor param disabled via feature flag - always send 0
  productParams.push(`smart_sensor=0`);
  
  // 2. Lang param
  const currentLang = localStorage.getItem('enkel-lang') || 'da';
  if (currentLang === 'en') {
    productParams.push('lang=en');
  }
  
  url += productParams.join('&');
  
  // 3. Tracking parameters (exclude paid/pacid — they're embedded in b44 click ID)
  const trackingData = getStoredTrackingParams();
  const trackingParams = [];
  const SKIP_IN_URL_BUILD = ['paid', 'pacid', 'clickid'];
  
  TRACKING_PARAMS.forEach(param => {
    if (trackingData[param] && !SKIP_IN_URL_BUILD.includes(param)) {
      trackingParams.push(`${param}=${encodeURIComponent(trackingData[param])}`);
    }
  });
  
  // Append metadata (stored as camelCase landingPage — sent as landing_page)
  if (trackingData.landingPage) {
    trackingParams.push(`landing_page=${encodeURIComponent(trackingData.landingPage)}`);
  }
  if (trackingData.referrer) {
    trackingParams.push(`referrer=${encodeURIComponent(trackingData.referrer)}`);
  }
  
  // Handle affiliate_click_id (may come as clickid or click_id)
  const affiliateClickIdBuild = trackingData.affiliate_click_id || trackingData.clickid || trackingData.click_id;
  if (affiliateClickIdBuild && !trackingParams.some(p => p.startsWith('affiliate_click_id='))) {
    trackingParams.push(`affiliate_click_id=${encodeURIComponent(affiliateClickIdBuild)}`);
  }
  
  if (trackingParams.length > 0) {
    url += '&' + trackingParams.join('&');
  }
  
  // 4. Pricing breakdown as single pipe-delimited parameter (ALWAYS LAST)
  // Format: consumption|zone|pricePerKwh|spotInclVat|spotExVat|surcharge|subMonthly|forbrug|distTariff|distSub|distTotal|transTariff|transSub|transTotal|elafgift|stategrid|dso
  if (params.pricing) {
    const p = params.pricing;
    const pxValues = [
      p.consumption || '',
      p.zone || '',
      p.pricePerKwh || '',
      p.spot || '',
      p.spotEx || '',
      p.surcharge || '0.00',
      p.sub || '',
      p.forbrug || '0.00',
      p.distTariff || '',
      p.distSub || '',
      p.distTotal || '',
      p.transTariff || '',
      p.transSub || '',
      p.transTotal || '',
      p.elafgift || '',
      p.stategrid || '',
      (p.dso || '').replace(/\s+/g, '-')
    ];
    url += '&px=' + encodeURIComponent(pxValues.join('|'));
  }
  
  // Generate t= tracking param based on final URL and prepend it
  const trackingCode = generateTrackingParam(url);
  
  // Use b44 click ID from session (generated on landing) or generate new one as fallback
  const storedB44Build = getStoredTrackingParams().b44_click_id;
  let b44ClickIdBuild = storedB44Build || generateB44ClickId(params.deal300 === true);
  
  // Campaign tracking takes priority over deal300
  // IMPORTANT: Never strip _pa_ or _rp_ suffix — partner tracking must survive
  if (params.campaignTrackingCode && !b44ClickIdBuild.includes('_pa_') && !b44ClickIdBuild.includes('_rp_')) {
    const baseId = b44ClickIdBuild.replace(/_deal300$/, '').replace(/_[A-Z0-9_]+_(?:nexblue_edge_2|zaptec_go|zaptec_go_2)$/, '');
    b44ClickIdBuild = `${baseId}_${params.campaignTrackingCode}`;
  } else if (params.deal300 === true && storedB44Build && !storedB44Build.includes('_deal300') && !b44ClickIdBuild.includes('_pa_') && !b44ClickIdBuild.includes('_rp_')) {
    b44ClickIdBuild = storedB44Build + '_deal300';
  }
  
  // Embed the active click ID as the FINAL suffix so it survives param-stripping redirects
  b44ClickIdBuild = appendClickIdToB44(b44ClickIdBuild);
  
  // Insert t= and b44= as the first parameters after the ?
  const urlParts = url.split('?');
  return urlParts[0] + '?t=' + trackingCode + '&b44=' + encodeURIComponent(b44ClickIdBuild) + '&' + urlParts[1];
}

/**
 * Helper to build pricing object from component state
 * All øre values should be INCL VAT (25%)
 * 
 * px format: consumption|zone|pricePerKwh|spotInclVat|spotExVat|surcharge|subMonthly|forbrug|distTariff|distSub|distTotal|transTariff|transSub|transTotal|elafgift|stategrid|dso
 */
export function buildPricingData({
  consumption,
  zone,
  pricePerKwh,
  spotOreInclVat,
  spotOreExVat,
  subscriptionMonthlyKr,
  forbrugEnabled,
  forbrugSurchargeOre,
  distTariffOreInclVat,
  distSubOreInclVat,
  distTotalOreInclVat,
  transTariffOreInclVat,
  transSubOreInclVat,
  transTotalOreInclVat,
  elafgiftOreInclVat,
  stategridOreInclVat,
  dsoName
}) {
  // Format subscription - don't add .00 if already formatted
  const subValue = String(subscriptionMonthlyKr);
  const formattedSub = subValue.includes('.') ? subValue : subValue + '.00';
  
  // Format forbrug surcharge - use dynamic value (1.6 øre = 0.016 kr) or 0
  const forbrugValue = forbrugEnabled ? (forbrugSurchargeOre || '2.00') : '0.00';
  
  return {
    consumption: String(consumption),
    zone: zone,
    pricePerKwh: pricePerKwh,
    spot: spotOreInclVat,
    spotEx: spotOreExVat,
    surcharge: '0.00',
    sub: formattedSub,
    forbrug: forbrugValue,
    distTariff: distTariffOreInclVat,
    distSub: distSubOreInclVat,
    distTotal: distTotalOreInclVat,
    transTariff: transTariffOreInclVat,
    transSub: transSubOreInclVat,
    transTotal: transTotalOreInclVat,
    elafgift: elafgiftOreInclVat,
    stategrid: stategridOreInclVat,
    dso: dsoName || 'Radius-Elnet'
  };
}